import { useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiFetch } from "../lib/api";
import { personDisplayName } from "@family/shared";
import type {
  FamilyMembersResponse,
  GroupMembersResponse,
  GroupsListResponse,
  PersonsListResponse,
} from "@family/shared";

interface GroupsAdminProps {
  familySlug: string;
}

export function GroupsAdmin({ familySlug }: GroupsAdminProps) {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newType, setNewType] = useState<"manual" | "branch">("manual");
  const [newRootPersonId, setNewRootPersonId] = useState("");
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);

  const groupsQuery = useQuery<GroupsListResponse>({
    queryKey: ["admin-groups", familySlug],
    queryFn: () => apiFetch<GroupsListResponse>(`/families/${familySlug}/groups`),
  });

  const membersQuery = useQuery<FamilyMembersResponse>({
    queryKey: ["members", familySlug],
    queryFn: () => apiFetch<FamilyMembersResponse>(`/families/${familySlug}/members`),
  });

  const personsQuery = useQuery<PersonsListResponse>({
    queryKey: ["persons", familySlug],
    queryFn: () => apiFetch<PersonsListResponse>(`/families/${familySlug}/persons`),
    enabled: newType === "branch",
  });

  const createGroup = useMutation({
    mutationFn: (body: any) =>
      apiFetch(`/families/${familySlug}/groups`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      setShowCreate(false);
      setNewName("");
      setNewDesc("");
      setNewType("manual");
      setNewRootPersonId("");
      void queryClient.invalidateQueries({ queryKey: ["admin-groups", familySlug] });
    },
  });

  const deleteGroup = useMutation({
    mutationFn: (groupId: string) =>
      apiFetch(`/families/${familySlug}/groups/${groupId}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-groups", familySlug] });
    },
  });

  const syncGroup = useMutation({
    mutationFn: (groupId: string) =>
      apiFetch(`/families/${familySlug}/groups/${groupId}/sync`, { method: "POST" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-groups", familySlug] });
      void queryClient.invalidateQueries({ queryKey: ["group-members"] });
    },
  });

  const groups = groupsQuery.data?.groups ?? [];
  const allMembers = membersQuery.data?.members ?? [];
  const persons = personsQuery.data?.persons ?? [];

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    createGroup.mutate({
      name: newName.trim(),
      description: newDesc.trim() || undefined,
      type: newType,
      rootPersonId: newType === "branch" ? newRootPersonId || undefined : undefined,
    });
  }

  return (
    <div className="stack">
      {groupsQuery.isLoading && <p className="text-muted">Loading…</p>}

      {groups.length === 0 && !groupsQuery.isLoading && (
        <p className="text-muted">No groups yet. Create one to get started.</p>
      )}

      {groups.map((g) => (
        <div key={g.id} className="card">
          <div className="row-between">
            <div>
              <strong>{g.name}</strong>
              <span className="badge" style={{ marginLeft: 8 }}>
                {g.type === "branch" ? "🌳 Branch" : "👥 Manual"}
              </span>
              <span className="text-subtle" style={{ marginLeft: 8, fontSize: "0.85rem" }}>
                {g.memberCount ?? 0} members
              </span>
            </div>
            <div style={{ display: "flex", gap: "var(--s-2)" }}>
              {g.type === "branch" && (
                <button
                  type="button"
                  className="btn-tertiary"
                  onClick={() => syncGroup.mutate(g.id)}
                  disabled={syncGroup.isPending}
                  style={{ fontSize: "0.85rem", padding: "4px 10px", minHeight: 30 }}
                >
                  {syncGroup.isPending ? "…" : "Sync"}
                </button>
              )}
              <button
                type="button"
                className="btn-tertiary"
                onClick={() => setExpandedGroup(expandedGroup === g.id ? null : g.id)}
                style={{ fontSize: "0.85rem", padding: "4px 10px", minHeight: 30 }}
              >
                {expandedGroup === g.id ? "Hide" : "Members"}
              </button>
              <button
                type="button"
                className="btn-tertiary"
                onClick={() => {
                  if (window.confirm(`Delete group "${g.name}"?`)) deleteGroup.mutate(g.id);
                }}
                disabled={deleteGroup.isPending}
                style={{ fontSize: "0.85rem", padding: "4px 10px", minHeight: 30, color: "var(--c-danger)" }}
              >
                Delete
              </button>
            </div>
          </div>
          {g.description && (
            <p className="text-subtle" style={{ fontSize: "0.85rem", marginTop: 4 }}>{g.description}</p>
          )}

          {expandedGroup === g.id && (
            <GroupMembersPanel
              familySlug={familySlug}
              groupId={g.id}
              groupType={g.type}
              allMembers={allMembers}
            />
          )}
        </div>
      ))}

      {!showCreate ? (
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setShowCreate(true)}
          style={{ alignSelf: "flex-start" }}
        >
          + Create group
        </button>
      ) : (
        <form onSubmit={handleCreate} className="card stack">
          <h4 style={{ margin: 0, fontSize: "1rem" }}>New group</h4>

          <div style={{ display: "flex", gap: 8 }}>
            {(["manual", "branch"] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setNewType(t)}
                style={{
                  padding: "6px 14px", minHeight: 34,
                  borderRadius: "var(--r-pill, 999px)",
                  border: "1px solid",
                  borderColor: newType === t ? "var(--c-blue)" : "var(--c-border)",
                  background: newType === t ? "var(--c-blue)" : "transparent",
                  color: newType === t ? "#fff" : "var(--c-text)",
                  fontWeight: newType === t ? 700 : 400,
                  fontSize: "0.85rem", cursor: "pointer",
                }}
              >
                {t === "manual" ? "👥 Manual" : "🌳 From tree"}
              </button>
            ))}
          </div>

          <div>
            <label htmlFor="grp-name">Name *</label>
            <input id="grp-name" type="text" value={newName}
              onChange={(e) => setNewName(e.target.value)} required
              placeholder={newType === "branch" ? "e.g. Descendants of Kidane" : "e.g. Cousins in Stockholm"} />
          </div>

          <div>
            <label htmlFor="grp-desc">Description</label>
            <input id="grp-desc" type="text" value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              placeholder="Optional description" />
          </div>

          {newType === "branch" && (
            <div>
              <label htmlFor="grp-root">Root person (ancestor)</label>
              <select id="grp-root" value={newRootPersonId}
                onChange={(e) => setNewRootPersonId(e.target.value)}
                style={{
                  width: "100%", padding: "10px 12px", fontSize: "1rem",
                  border: "1px solid var(--c-border-strong)", borderRadius: 4,
                  background: "var(--c-bg)", color: "var(--c-text)",
                }}>
                <option value="">— Select person —</option>
                {persons.map((p) => (
                  <option key={p.id} value={p.id}>
                    {personDisplayName(p)}
                    {p.birthDate ? ` (${p.birthDate.slice(0, 4)})` : ""}
                  </option>
                ))}
              </select>
              <p className="text-subtle" style={{ fontSize: "0.8rem", marginTop: 4 }}>
                All descendants of this person (who have user accounts) will be auto-added.
              </p>
            </div>
          )}

          {createGroup.error && <p className="text-error">{createGroup.error.message}</p>}

          <div style={{ display: "flex", gap: "var(--s-3)" }}>
            <button type="submit" disabled={createGroup.isPending || !newName.trim()}>
              {createGroup.isPending ? "Creating…" : "Create group"}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setShowCreate(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function GroupMembersPanel({
  familySlug,
  groupId,
  groupType,
  allMembers,
}: {
  familySlug: string;
  groupId: string;
  groupType: string;
  allMembers: { userId: string; displayName: string | null; email: string | null }[];
}) {
  const queryClient = useQueryClient();
  const [addUserId, setAddUserId] = useState("");

  const membersQuery = useQuery<GroupMembersResponse>({
    queryKey: ["group-members", familySlug, groupId],
    queryFn: () =>
      apiFetch<GroupMembersResponse>(`/families/${familySlug}/groups/${groupId}/members`),
  });

  const addMember = useMutation({
    mutationFn: (userId: string) =>
      apiFetch(`/families/${familySlug}/groups/${groupId}/members`, {
        method: "POST",
        body: JSON.stringify({ userIds: [userId] }),
      }),
    onSuccess: () => {
      setAddUserId("");
      void queryClient.invalidateQueries({ queryKey: ["group-members", familySlug, groupId] });
      void queryClient.invalidateQueries({ queryKey: ["admin-groups", familySlug] });
    },
  });

  const removeMember = useMutation({
    mutationFn: (userId: string) =>
      apiFetch(`/families/${familySlug}/groups/${groupId}/members/${userId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["group-members", familySlug, groupId] });
      void queryClient.invalidateQueries({ queryKey: ["admin-groups", familySlug] });
    },
  });

  const members = membersQuery.data?.members ?? [];
  const memberIds = new Set(members.map((m) => m.userId));
  const nonMembers = allMembers.filter((m) => !memberIds.has(m.userId));

  return (
    <div style={{ marginTop: "var(--s-3)", paddingTop: "var(--s-3)", borderTop: "1px solid var(--c-border)" }}>
      {membersQuery.isLoading && <p className="text-muted" style={{ fontSize: "0.85rem" }}>Loading…</p>}

      {members.length === 0 && !membersQuery.isLoading && (
        <p className="text-muted" style={{ fontSize: "0.85rem" }}>No members yet.</p>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: "var(--s-2)" }}>
        {members.map((m) => (
          <span key={m.userId} style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            padding: "4px 10px", borderRadius: "var(--r-pill)",
            background: "var(--c-bg-alt)", border: "1px solid var(--c-border)",
            fontSize: "0.85rem",
          }}>
            {m.displayName ?? m.email ?? m.userId.slice(0, 8)}
            <button
              type="button"
              onClick={() => removeMember.mutate(m.userId)}
              disabled={removeMember.isPending}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: "var(--c-text-subtle)", fontSize: "0.9rem", padding: "0 2px",
                minHeight: 0,
              }}
            >
              ✕
            </button>
          </span>
        ))}
      </div>

      {groupType === "manual" && nonMembers.length > 0 && (
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select
            value={addUserId}
            onChange={(e) => setAddUserId(e.target.value)}
            style={{
              flex: 1, padding: "6px 10px", fontSize: "0.85rem",
              border: "1px solid var(--c-border-strong)", borderRadius: 6,
              background: "var(--c-bg)", color: "var(--c-text)",
            }}
          >
            <option value="">Add member…</option>
            {nonMembers.map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.displayName ?? m.email ?? m.userId.slice(0, 8)}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => { if (addUserId) addMember.mutate(addUserId); }}
            disabled={!addUserId || addMember.isPending}
            style={{ padding: "6px 14px", fontSize: "0.85rem", borderRadius: 6, minHeight: 32 }}
          >
            Add
          </button>
        </div>
      )}
    </div>
  );
}
